import { Suspense, lazy, useCallback, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import {
  RiArrowDownSLine,
  RiArrowRightLine,
  RiPlayCircleLine,
} from '@remixicon/react';

import {
  type ToolGroupEntry,
  createToolExtension,
  mcpTool,
} from '@spotify/plugin-confidence-chat-react';

import { recordingsPlugin } from '../plugin';
import { RecordingDrawer } from './RecordingDrawer';
import classes from './RecordingToolExtension.module.css';
import { getRecordingLabel } from './types';
import type { RecordingHit } from './types';

const LazyRecordingPlayer = lazy(() =>
  import('@spotify/confidence-recordings-react').then(m => ({
    default: m.RecordingPlayer,
  })),
);

type SearchRecordingsResult = {
  query: string;
  total: number;
  recordings: RecordingHit[];
};

const MAX_PREVIEWS = 10;

const parseSearchResult = (
  entry: ToolGroupEntry,
): SearchRecordingsResult | null => {
  if (!entry.result?.content) return null;
  try {
    const parsed = JSON.parse(entry.result.content) as SearchRecordingsResult;
    if (Array.isArray(parsed.recordings)) return parsed;
  } catch {
    /* not valid JSON */
  }
  return null;
};

const RecordingToolRenderer = ({ entry }: { entry: ToolGroupEntry }) => {
  const result = parseSearchResult(entry);
  const recordings =
    result?.recordings.filter(r => r.preview).slice(0, MAX_PREVIEWS) ?? [];

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleClose = useCallback(() => setActiveIndex(null), []);

  if (recordings.length === 0) return null;

  const topRecording = recordings[0];

  return (
    <div className={classes.wrapper}>
      <div className={classes.preview}>
        <div className={classes.previewHeader}>
          {topRecording.link ? (
            <a
              href={topRecording.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.previewLink}
            >
              {getRecordingLabel(topRecording)}
              <span className={classes.previewScore}>
                ({(topRecording.score * 100).toFixed(1)}% relevance)
              </span>
              <RiArrowRightLine size={14} />
            </a>
          ) : (
            <span className={classes.previewName}>
              {getRecordingLabel(topRecording)}
            </span>
          )}
        </div>
        <div className={classes.previewPlayer}>
          <Suspense fallback={<div className={classes.previewPlaceholder} />}>
            <LazyRecordingPlayer
              src={topRecording.preview!}
              variant="simplified"
              autoplay={false}
            />
          </Suspense>
        </div>
      </div>
      {recordings.length > 1 && (
        <>
          <Accordion defaultExpanded className={classes.accordion}>
            <AccordionSummary
              expandIcon={<RiArrowDownSLine size={16} />}
              className={classes.accordionSummary}
              classes={{ content: classes.accordionSummaryContent }}
            >
              <Typography
                variant="subtitle2"
                component="span"
                className={classes.tableTitle}
              >
                Related recordings ({recordings.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Table size="small" className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>#</TableCell>
                    <TableCell className={classes.headerCell}>
                      Session
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      Relevance
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      Preview
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recordings.map((recording, index) => (
                    <TableRow key={recording.name}>
                      <TableCell className={classes.numberCell}>
                        {index + 1}
                      </TableCell>
                      <TableCell className={classes.sessionCell}>
                        {getRecordingLabel(recording)}
                      </TableCell>
                      <TableCell className={classes.relevanceCell}>
                        {(recording.score * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell>
                        <span
                          role="button"
                          tabIndex={0}
                          className={classes.watchLink}
                          onClick={() => setActiveIndex(index)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setActiveIndex(index);
                            }
                          }}
                        >
                          Watch <RiArrowRightLine size={14} />
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
          <RecordingDrawer
            recordings={recordings}
            activeIndex={activeIndex}
            onClose={handleClose}
            onNavigate={setActiveIndex}
          />
        </>
      )}
    </div>
  );
};

export const RecordingToolExtension = recordingsPlugin.provide(
  createToolExtension({
    id: 'recording-preview',
    predicate: mcpTool('semantic_search_recordings'),
    icon: RiPlayCircleLine,
    display: 'prominent',
    render: () => null,
    renderTool: entry => <RecordingToolRenderer entry={entry} />,
  }),
);
